/* eslint-disable camelcase */
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useCurrentSite } from '@hotjar/site';
import { useSiteLocalStorageJSONValue } from '@hotjar/insights-common';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useResponsesLimit, useToolsCountQuery } from '@hotjar/feedback';
import type { Site } from '@hotjar/site';
import type { UpgradeInfo } from '../../../domain/common/Responses/ResponseLimitInfo';
import { ResponseUpgradeAlertStateless } from './ResponseUpgradeAlertStateless';
import { ResponseLimitAlertStateless } from './ResponseLimitAlertStateless';

const Container = styled.div`
  margin: 0 ${tokens.spacer.x5} ${tokens.spacer.x2} ${tokens.spacer.x5};
`;

export type ResponseLimitLocation =
  | 'feedback-responses'
  | 'feedback-wizard'
  | 'feedback-results'
  | 'survey-results'
  | 'survey-performance'
  | 'survey-responses'
  | 'survey-wizard'
  | 'survey-list';

type FeatureGroup = 'surveys' | 'feedback';

export interface ShowResponseLimitAlertProps {
  location: ResponseLimitLocation;
  featureGroup: FeatureGroup;
}

export interface ResponseLimitAlertProps {
  location: ResponseLimitLocation;
  rolloverDate: Date | null;
  overHalfRemaining: boolean;
  upgradeAlertHidden: boolean;
  site: Site;
  remaining: number;
  limitChangedEpochTime: number;
  limit: number;
  setHideUpgradeInfo: (hideUpgradedInfo: UpgradeInfo | undefined) => void;
}

export const useShowResponseLimitAlert = ({
  location,
  featureGroup,
}: ShowResponseLimitAlertProps):
  | { visibility: 'loading' }
  | { visibility: 'hidden' }
  | { visibility: 'visible'; props: ResponseLimitAlertProps } => {
  const { data: site } = useCurrentSite();
  const storage = useSiteLocalStorageJSONValue<number, UpgradeInfo>(
    'responseLimitUpgradeInfo',
    site?.id,
  );
  const { data: widgetCount } = useToolsCountQuery(site, featureGroup);
  const { responseLimitInfo, rolloverDate } = useResponsesLimit();
  if (storage === undefined || !site || !responseLimitInfo || widgetCount === undefined)
    return { visibility: 'loading' };

  if (responseLimitInfo.unlimited || widgetCount === 0) return { visibility: 'hidden' };

  const { remaining, limit } = responseLimitInfo;

  const hasFullInfo = 'limit_changed_type' in responseLimitInfo;

  if (!hasFullInfo) {
    return { visibility: 'hidden' };
  }
  // check if remaining responses are more than 50% of total
  const overHalfRemaining = remaining * 2 > limit;

  const [hideUpgradedInfo, setHideUpgradeInfo] = storage;

  const { date, hide } = hideUpgradedInfo ?? {};
  const upgradeAlertHidden =
    responseLimitInfo.limit_changed_type !== 'upgrade' ||
    (hideUpgradedInfo !== undefined &&
      date === responseLimitInfo.limit_changed_epoch_time &&
      hide === true);

  if (overHalfRemaining && upgradeAlertHidden) return { visibility: 'hidden' };

  return {
    visibility: 'visible',
    props: {
      location,
      rolloverDate,
      overHalfRemaining,
      upgradeAlertHidden,
      site,
      remaining,
      limitChangedEpochTime: responseLimitInfo.limit_changed_epoch_time,
      limit,
      setHideUpgradeInfo,
    },
  };
};

export const ResponseLimitAlert = ({
  location,
  rolloverDate,
  overHalfRemaining,
  upgradeAlertHidden,
  site,
  remaining,
  limitChangedEpochTime,
  limit,
  setHideUpgradeInfo,
}: ResponseLimitAlertProps): JSX.Element | null => {
  const { click: trackClick } = useTrack();

  const showUpgradeAlert = overHalfRemaining && !upgradeAlertHidden;

  const onDismiss = () => {
    setHideUpgradeInfo({ date: limitChangedEpochTime, hide: true });
  };

  const openUpsellModal = () => {
    trackClick('Get more responses', location, {});
    upsell({
      site,
      referrer: remaining
        ? `${ProductType.Feedback}.response_limit_50_percent`
        : `${ProductType.Feedback}.response_limit_100_percent`,
    }).then(noop, noop);
  };

  /* TODO Replace these legacy classes when we migrate the responses views */
  return (
    <Container>
      {!overHalfRemaining && (
        <ResponseLimitAlertStateless
          effectiveAskPlan={site.effectiveAskPlan}
          remaining={remaining}
          total={limit}
          rolloverDate={rolloverDate}
          onAction={openUpsellModal}
        />
      )}
      {showUpgradeAlert && (
        <ResponseUpgradeAlertStateless
          total={limit}
          upgradeDate={limitChangedEpochTime}
          onDismiss={onDismiss}
        />
      )}
    </Container>
  );
};

export const StatefulResponseLimitAlert = (
  props: ShowResponseLimitAlertProps,
): JSX.Element | null => {
  const state = useShowResponseLimitAlert(props);

  if (state.visibility !== 'visible') return null;

  return <ResponseLimitAlert {...state.props} />;
};
